/**
 * The "Choose your provider" list and the "Other (IMAP/SMTP)" form, shared by web, desktop and
 * mobile so they offer exactly the same choices and send exactly the same requests.
 */
import type { MailDetection } from './mail-detect.js';
import { MAIL_PRESETS, UNSUPPORTED_MAIL_PROVIDERS, mailPreset, type MailPreset } from './mail-presets.js';
import { mailPortFor, type MailSecurity } from './mail-servers.js';
import { PROVIDERS } from './providers.js';
import type { CustomMailServerInput } from './schemas.js';

export type ProviderChoice =
  | { kind: 'oauth'; key: string; provider: 'google' | 'microsoft' | 'zoho'; label: string; hint: string; terms: readonly string[] }
  | { kind: 'spacemail'; key: string; label: string; hint: string; terms: readonly string[] }
  | { kind: 'preset'; key: string; presetId: string; label: string; hint: string; terms: readonly string[] }
  | { kind: 'custom'; key: string; label: string; hint: string; terms: readonly string[] }
  | { kind: 'unsupported'; key: string; label: string; hint: string; reason: string; terms: readonly string[] };

function presetHint(p: MailPreset): string {
  if (p.appPassword === 'required') return `Email address and ${p.passwordName}`;
  return 'Email address and password';
}

/** Every way to add a mailbox, in display order. */
export function providerChoices(): ProviderChoice[] {
  return [
    { kind: 'oauth', key: 'google', provider: 'google', label: PROVIDERS.google.label, hint: 'Sign in with Google', terms: ['gmail', 'google workspace', 'googlemail.com', 'gmail.com'] },
    { kind: 'oauth', key: 'microsoft', provider: 'microsoft', label: PROVIDERS.microsoft.label, hint: 'Sign in with Microsoft', terms: ['outlook', 'hotmail', 'live', 'msn', 'office 365', 'microsoft 365', 'exchange'] },
    { kind: 'oauth', key: 'zoho', provider: 'zoho', label: PROVIDERS.zoho.label, hint: 'Sign in with Zoho', terms: ['zoho', 'zohomail'] },
    { kind: 'spacemail', key: 'spacemail', label: PROVIDERS.spacemail.label, hint: 'Email address and mailbox password', terms: ['spaceship', 'spacemail'] },
    ...MAIL_PRESETS.map((p): ProviderChoice => ({ kind: 'preset', key: `preset:${p.id}`, presetId: p.id, label: p.label, hint: presetHint(p), terms: [p.shortLabel, ...p.domains] })),
    { kind: 'custom', key: 'custom', label: 'Other (IMAP/SMTP)', hint: 'Enter your provider’s incoming and outgoing mail servers', terms: ['imap', 'smtp', 'other', 'custom', 'self-hosted'] },
    ...UNSUPPORTED_MAIL_PROVIDERS.map((u): ProviderChoice => ({ kind: 'unsupported', key: `unsupported:${u.id}`, label: u.label, hint: 'Can’t be connected', reason: u.reason, terms: [...u.domains] })),
  ];
}

/** Case-insensitive search over names, short names and address domains. */
export function filterProviderChoices(choices: readonly ProviderChoice[], query: string): ProviderChoice[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...choices];
  return choices.filter((c) => c.label.toLowerCase().includes(q) || c.terms.some((t) => t.toLowerCase().includes(q)) || c.kind === 'custom');
}

/** The choice a detection result points to (for headings and back navigation). */
export function choiceForDetection(d: MailDetection): ProviderChoice | null {
  const all = providerChoices();
  switch (d.kind) {
    case 'oauth':
      return all.find((c) => c.kind === 'oauth' && c.provider === d.provider) ?? null;
    case 'preset':
      return mailPreset(d.presetId) ? (all.find((c) => c.kind === 'preset' && c.presetId === d.presetId) ?? null) : null;
    case 'spacemail':
      return all.find((c) => c.kind === 'spacemail') ?? null;
    case 'custom':
      return all.find((c) => c.kind === 'custom') ?? null;
    case 'unsupported':
      return all.find((c) => c.kind === 'unsupported' && c.label === d.provider) ?? null;
  }
}

/** What the "Other (IMAP/SMTP)" form holds. Ports follow from the security choice. */
export interface CustomServerForm {
  imapHost: string;
  imapSecurity: MailSecurity;
  smtpHost: string;
  smtpSecurity: MailSecurity;
  username: string;
  caldavUrl: string;
}

export const EMPTY_CUSTOM_SERVER_FORM: CustomServerForm = Object.freeze({ imapHost: '', imapSecurity: 'implicit-tls', smtpHost: '', smtpSecurity: 'implicit-tls', username: '', caldavUrl: '' });

export const SECURITY_LABEL: Readonly<Record<'imap' | 'smtp', Readonly<Record<MailSecurity, string>>>> = Object.freeze({
  imap: Object.freeze({ 'implicit-tls': 'SSL/TLS (port 993)', starttls: 'STARTTLS (port 143)' }),
  smtp: Object.freeze({ 'implicit-tls': 'SSL/TLS (port 465)', starttls: 'STARTTLS (port 587)' }),
});

export function customServerRequest(f: CustomServerForm): CustomMailServerInput {
  return {
    imap: { host: f.imapHost.trim(), port: mailPortFor('imap', f.imapSecurity), security: f.imapSecurity },
    smtp: { host: f.smtpHost.trim(), port: mailPortFor('smtp', f.smtpSecurity), security: f.smtpSecurity },
    ...(f.username.trim() ? { username: f.username.trim() } : {}),
    ...(f.caldavUrl.trim() ? { caldavUrl: f.caldavUrl.trim() } : {}),
  };
}

/** Maps validation issue paths of an IMAP connect request to custom-form fields. */
export function customFieldForPath(path: string): keyof CustomServerForm | 'email' | 'password' | null {
  if (path === 'email' || path === 'password') return path;
  if (path.startsWith('custom.imap')) return 'imapHost';
  if (path.startsWith('custom.smtp')) return 'smtpHost';
  if (path === 'custom.username') return 'username';
  if (path === 'custom.caldavUrl') return 'caldavUrl';
  return null;
}

/** "App password", "Secure mail key": the label for a preset's password field. */
export function passwordFieldLabel(p: MailPreset | null): string {
  if (!p || p.appPassword !== 'required') return 'Password';
  return p.passwordName.charAt(0).toUpperCase() + p.passwordName.slice(1);
}
